import { Box, Stack } from '@mui/material';
import pluralize from 'pluralize';
import { useState, useCallback, useMemo, ReactNode, useRef, useEffect } from 'react';
import { filterItemsBySearch } from '../../../../../../lib/filterItemsBySearch';
import { useCampaignFieldsAndEnrollmentRules } from './useCampaignFieldsAndEnrollmentRules';
import { Commands } from '../../../../../../types/enums/Common';
import { FlexBox } from '../../../../../common/FlexBox';
import RestrictedTooltip from '../../../../../common/permissions/RestrictedTooltip';
import { usePermissions } from '../../../../../../permissions/usePermissions';
import { SearchInput } from '../../../../../common/SearchInput';
import { CampaignMemberStatusProvider } from '../../../campaign-member-status/CampaignMemberStatusProvider';
import { globalSearchSx } from '../../../canvasPanelConsts';
import { HubspotFilters } from '../../HubspotFilters';
import { useLoadSfCampaignFieldsForHubspot } from '../../useLoadSfCampaignFieldsForHubspot';
import { CampaignFields } from '../campaignTypes';
import { HubspotCampaignContent } from './HubspotCampaignContent';
import { HubspotCampaignCtxProvider } from '../HubspotCampaignCtx';
import { HubspotCampaignDialog } from '../HubspotCampaignDialog';
import { HubspotFilterTypes } from './HubspotCanvasPanel';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import { cloneDeep, keyBy } from 'lodash';
import { campaignFieldsToCampaign } from './helper';
import { useAreCampaignsReady } from '../useAreCampaignsReady';
import { actionsMenuItems, campaignColumns } from './utils';
import { Enrollment } from '@sweep-io/sweep-design/dist/icons';
import { useExpandedMode } from '../../../../configuration-canvas/panels-reserve-space/useExpandedMode';
import { ACTION_BUTTONS_KEY, DataTableId } from '../../../../../common/table/utils';
import { SortOrder } from '../../../../../common/types';
import { SEARCH_BOX_WIDTH } from '../../../../../Automations/helper';
import { DataTable } from '../../../../../common/table/DataTable';
import { ActionsMenu } from '../../../../../common/actions-menu/ActionsMenu';
import { CenteredCircularProgress } from '../../../../../common/CenteredCircularProgress';
import { PAGE_SIDE_PADDING } from '../../../../../../constants';
import { DataTableRow } from '../../../../../common/table/TableTypes';
import { WorkflowDto } from '../../types';
import { useHubspotFiltersContext } from '../../HubspotFiltersContext';

const disabledActionsMenuItems: any = [];

export interface RowWithActions extends DataTableRow {
  name: ReactNode;
  _name: string;
  status: string;
  type: string;
  startDate: string;
  endDate: string;
  expectedRevenue: string;
  _lastModifiedDate: string;
}

const EnrolmentRulesByCampaign = ({ enrollmentRules }: { enrollmentRules: WorkflowDto[] }) => {
  const campaignActiveWorkflows = enrollmentRules.length;
  if (campaignActiveWorkflows === 0) {
    return null;
  }
  return (
    <FlexBox alignItems="center" gap={1} mt={'2px'}>
      <Enrollment color={colors.mint[500]} />
      <Typography variant="body" color={colors.grey[700]}>
        {campaignActiveWorkflows} Active Enrollment {pluralize('Rule', campaignActiveWorkflows)}
      </Typography>
    </FlexBox>
  );
};
export const PanelCampaignsTab = ({ crmOrgId, hsOrgId }: { crmOrgId: string; hsOrgId: string }) => {
  const areCampaignsReady = useAreCampaignsReady();
  const allCampaigns = useCampaignFieldsAndEnrollmentRules();
  const [campaignDialog, setCampaignDialog] = useState<{
    open: boolean;
    campaignFields?: CampaignFields;
  }>();
  const editCampaignTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duplicatedCampaignTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { campaignStatusOptions, campaignTypeOptions, campaignObjectFields } =
    useLoadSfCampaignFieldsForHubspot();

  const { filterValues, clearAllFilterValues } = useHubspotFiltersContext();

  useEffect(() => {
    //clear all filters when the component unmounts
    return () => clearAllFilterValues();
  }, [clearAllFilterValues]);

  const closeCampaignDialog = useCallback(() => setCampaignDialog(undefined), []);

  const newCampaign = useCallback(() => setCampaignDialog({ open: true }), []);

  const editCampaign = useCallback(
    (campaignFields: CampaignFields) => () => {
      setCampaignDialog({ open: false });
      editCampaignTimeOutRef.current = setTimeout(() => {
        setCampaignDialog({ open: true, campaignFields });
      }, 0);
    },
    [],
  );

  const duplicateCampaign = useCallback((campaignFields: CampaignFields) => {
    setCampaignDialog({ open: false });
    // We need a timeout here to ensure that the dialog is reloaded.
    // The dialog is uncontrolled, witch means it's not sensitive to changes in the props,
    // So it need to be remounted. We achieve this my adding a timeout with 0 ms.

    duplicatedCampaignTimeOutRef.current = setTimeout(() => {
      const newCampaignFields = cloneDeep(campaignFields);
      const campaignNameIdx = newCampaignFields.findIndex((field) => field.name === 'Name');
      if (campaignNameIdx !== -1) {
        newCampaignFields[campaignNameIdx].value =
          `${newCampaignFields[campaignNameIdx].value} (Copy)`;
      }
      // deletes ID
      const campaignIdIdx = campaignFields.findIndex((field) => field.name === 'Id');
      if (campaignIdIdx !== -1) {
        newCampaignFields.splice(campaignIdIdx, 1);
      }
      setCampaignDialog({
        open: true,
        campaignFields: newCampaignFields,
      });
    }, 0);
  }, []);

  useEffect(() => {
    return () => {
      if (duplicatedCampaignTimeOutRef.current) {
        clearTimeout(duplicatedCampaignTimeOutRef.current);
      }
      if (editCampaignTimeOutRef.current) {
        clearTimeout(editCampaignTimeOutRef.current);
      }
    };
  }, [duplicatedCampaignTimeOutRef, editCampaignTimeOutRef]);

  const [search, setSearch] = useState('');
  const campaignsFilters = allCampaigns?.filter(({ campaignFields, enrollmentRules }) => {
    const _campaign = campaignFieldsToCampaign(campaignFields);

    const activityFilter = filterValues[HubspotFilterTypes.ACTIVITY];
    if (activityFilter && activityFilter.selectedValues.length > 0) {
      if (_campaign.IsActive) {
        if (!activityFilter.selectedValues.includes('active')) {
          return false;
        }
      } else {
        if (!activityFilter.selectedValues.includes('inactive')) {
          return false;
        }
      }
    }
    const statusFilter = filterValues[HubspotFilterTypes.CAMPAIGN_STATUS];
    if (statusFilter && statusFilter.selectedValues.length > 0) {
      if (!statusFilter.selectedValues.includes(_campaign.Status || '')) {
        return false;
      }
    }

    const typeFilter = filterValues[HubspotFilterTypes.CAMPAIGN_TYPE];
    if (typeFilter && typeFilter.selectedValues.length > 0) {
      if (!typeFilter.selectedValues.includes(_campaign.Type || '')) {
        return false;
      }
    }
    const enrollmentRuleFilter = filterValues[HubspotFilterTypes.ENROLLMENT_RULE];
    if (enrollmentRuleFilter && enrollmentRuleFilter.selectedValues.length === 1) {
      if (enrollmentRuleFilter.selectedValues.includes('with_enrollment_rule')) {
        if (enrollmentRules.length === 0) {
          return false;
        }
      }
      if (enrollmentRuleFilter.selectedValues.includes('without_enrollment_rule')) {
        if (enrollmentRules.length > 0) {
          return false;
        }
      }
    }
    return true;
  });
  const filteredCampaigns = filterItemsBySearch(
    campaignsFilters || [],
    search,
    (item) => item.campaignFields.find((field) => field.name === 'Name')?.value as string,
  );

  const { evaluatePermissions } = usePermissions();
  const [isCreateCampaignsAllowed] = evaluatePermissions([{ permission: 'create:campaigns', crmOrgId }]);
  const { isExpandedMode } = useExpandedMode();

  const newButtonJsx = (
    <RestrictedTooltip
      validation={{ type: 'explicit', allowed: isCreateCampaignsAllowed }}
      notAllowedTitle={'To create Hubspot campaigns, please contact your admin'}
      component={(isAllowed) => (
        <Stack>
          <Button
            dataTestId="create-new-campaign-button"
            startIconName="Plus"
            size="small"
            onClick={newCampaign}
            disabled={!isAllowed}
          >
            New
          </Button>
        </Stack>
      )}
    />
  );

  const searchInputJsx = (
    <SearchInput
      TextFieldProps={{
        value: search,
        placeholder: 'Search',
        onChange: (e) => setSearch(e.target.value),
        fullWidth: true,
      }}
      withFixedMagnifyingGlassIcon={true}
      onClearButtonClick={() => setSearch('')}
      size="small"
    />
  );

  const onActionMenuClick = useCallback(
    (action: any, campaignFields: CampaignFields) => {
      switch (action) {
        case Commands.Edit:
          editCampaign(campaignFields)();
          break;
        case Commands.Duplicate:
          duplicateCampaign(campaignFields);
          break;
      }
    },
    [editCampaign, duplicateCampaign],
  );

  const maybeRenderLoader = () => {
    if (!areCampaignsReady) {
      return <CenteredCircularProgress />;
    }
  };

  const actions = isCreateCampaignsAllowed ? actionsMenuItems : disabledActionsMenuItems;
  const rowWithActions: RowWithActions[] = useMemo(
    () =>
      filteredCampaigns.map(({ campaignFields, enrollmentRules, lastModifiedDate }) => {
        const _campaign = keyBy(campaignFields, 'name');
        const campaignId = _campaign['Id'].value as string;
        const name = _campaign['Name'].value as string;
        const status = _campaign['Status']?.value as string;
        const type = _campaign['Type'].value as string;
        const startDate = _campaign['StartDate'].value as string;
        const endDate = _campaign['EndDate'].value as string;
        const expectedRevenue = _campaign['ExpectedRevenue'].value as string;

        return {
          name: (
            <Stack direction="column" gap={0.5}>
              <Typography variant="body" color={colors.grey[700]}>
                {name}
              </Typography>
              <EnrolmentRulesByCampaign enrollmentRules={enrollmentRules} />
            </Stack>
          ),
          _name: name,
          id: campaignId,
          status,
          type,
          startDate,
          endDate,
          expectedRevenue,
          [ACTION_BUTTONS_KEY]: (
            <ActionsMenu
              actions={actions}
              menuPlacement="bottom"
              onClick={(action) => onActionMenuClick(action.value, campaignFields)}
            />
          ),
          _lastModifiedDate: lastModifiedDate || '',
        };
      }),
    [filteredCampaigns, actions, onActionMenuClick],
  );

  const maybeRenderCampaignsTable = () => {
    if (!isExpandedMode) {
      return null;
    }
    if (areCampaignsReady) {
      return (
        <Stack id="panel-campaigns-tab" height="calc(100% - 35px)" pb={2}>
          <Stack
            sx={{
              ml: PAGE_SIDE_PADDING + 'px',
              mr: PAGE_SIDE_PADDING + 'px',
              mb: isExpandedMode ? 3 : 1,
              ...globalSearchSx(false),
            }}
          >
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
            >
              <Box flex={1} display="flex" gap={1} flexDirection="row" alignItems="center">
                <Box width={SEARCH_BOX_WIDTH}>{searchInputJsx}</Box>
                <HubspotFilters />
              </Box>
              {newButtonJsx}
            </Stack>
          </Stack>

          <Stack
            sx={{
              height: '100%',
              pl: PAGE_SIDE_PADDING + 'px',
              pr: PAGE_SIDE_PADDING + 'px',
              overflow: 'hidden',
            }}
          >
            <DataTable
              tableId={DataTableId.HubspotCampaignsTable}
              onRowClick={(
                e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
                row: RowWithActions,
              ) => {
                const campaigns = filteredCampaigns.find(({ campaignFields }) => {
                  const _campaign = keyBy(campaignFields, 'name');
                  const campaignId = _campaign['Id'].value as string;
                  return campaignId === row.id;
                });

                if (campaigns) {
                  editCampaign(campaigns.campaignFields)();
                }
              }}
              rows={rowWithActions}
              columns={campaignColumns}
              defaultSortState={{
                sortBy: '_lastModifiedDate',
                sortOrder: SortOrder.DESC,
              }}
            />
          </Stack>
        </Stack>
      );
    }
  };

  const maybeRenderCampaignForm = () => {
    if (campaignDialog?.open) {
      return (
        <HubspotCampaignCtxProvider
          campaignStatusOptions={campaignStatusOptions}
          campaignTypeOptions={campaignTypeOptions}
          campaignObjectFields={campaignObjectFields}
          crmOrgId={crmOrgId}
          hsOrgId={hsOrgId}
          objectType="Contact"
        >
          <HubspotCampaignDialog
            isOpen={true}
            onClose={closeCampaignDialog}
            isShowWideScreenPopup={isExpandedMode}
          >
            <CampaignMemberStatusProvider>
              <HubspotCampaignContent initialCampaignFields={campaignDialog.campaignFields} />
            </CampaignMemberStatusProvider>
          </HubspotCampaignDialog>
        </HubspotCampaignCtxProvider>
      );
    }
  };

  return (
    <>
      {maybeRenderLoader()}
      {maybeRenderCampaignsTable()}
      {maybeRenderCampaignForm()}
    </>
  );
};
